import type { HostTenantListItem, HostTenantStatus } from '../../tenants/api/tenants';
import { normalizeTenantStatus } from '../../tenants/api/tenants';

export { normalizeTenantStatus };

export const LICENSE_WARNING_DAYS = 14;

export const DEFAULT_TRIAL_DAYS = 14;

const STATUS_LABELS: Record<HostTenantStatus, string> = {
  Requested: 'Requested',
  Approved: 'Approved',
  Active: 'Active',
  Expired: 'Expired',
  Rejected: 'Rejected',
};

const STATUS_COLORS: Record<HostTenantStatus, string> = {
  Requested: 'processing',
  Approved: 'warning',
  Active: 'success',
  Expired: 'error',
  Rejected: 'default',
};

export function tenantStatusLabel(status: HostTenantStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function tenantStatusTagColor(status: HostTenantStatus): string {
  return STATUS_COLORS[status] ?? 'default';
}

export function formatTenantDate(value?: string): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function primaryContactName(row: HostTenantListItem): string {
  return `${row.primaryContactFirstName} ${row.primaryContactLastName}`.trim();
}

export type LicenseFilter = 'all' | 'expiring' | 'overdue' | 'none';

export function matchesLicenseFilter(row: HostTenantListItem, filter: LicenseFilter): boolean {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'none') {
    return !row.licenseEndsAtUtc;
  }

  if (!row.licenseEndsAtUtc || normalizeTenantStatus(row.status) !== 'Active') {
    return false;
  }

  const endsAt = new Date(row.licenseEndsAtUtc);
  if (Number.isNaN(endsAt.getTime())) {
    return false;
  }

  const now = Date.now();
  const warningMs = LICENSE_WARNING_DAYS * 24 * 60 * 60 * 1000;

  if (filter === 'overdue') {
    return endsAt.getTime() < now;
  }

  return endsAt.getTime() >= now && endsAt.getTime() <= now + warningMs;
}

export function licenseDisplayState(row: HostTenantListItem): 'none' | 'ok' | 'expiring' | 'overdue' {
  if (!row.licenseEndsAtUtc) {
    return 'none';
  }

  if (normalizeTenantStatus(row.status) !== 'Active') {
    return 'ok';
  }

  const endsAt = new Date(row.licenseEndsAtUtc);
  if (Number.isNaN(endsAt.getTime())) {
    return 'none';
  }

  const now = Date.now();
  const warningMs = LICENSE_WARNING_DAYS * 24 * 60 * 60 * 1000;

  if (endsAt.getTime() < now) {
    return 'overdue';
  }

  if (endsAt.getTime() <= now + warningMs) {
    return 'expiring';
  }

  return 'ok';
}
